import React, { useEffect, useRef } from "react";

/**
 * ViewportVideo — mykkä looppivideo, joka toistuu vain näkymässä.
 *
 * Selain ei hae videota ennen kuin se tulee näkymään (preload="none", ei
 * autoplay-attribuuttia). Kun vähintään neljännes videosta on näkyvissä,
 * IntersectionObserver käynnistää sen; kun se poistuu näkymästä kokonaan,
 * se pysähtyy.
 * Siihen asti näkyy poster-still (videon ensimmäinen kuva), joten kehys ei
 * ole koskaan tyhjä hitaallakaan yhteydellä.
 *
 * Posterit rakennetaan scripts/build-posters.swift-skriptillä, ja polku
 * päätellään videon nimestä: /videos/<nimi>.mp4 → /images/posters/<nimi>.jpg.
 *
 * muted + playsInline pitävät iOS:n inline-toiston sallittuna ilman
 * käyttäjän elettä. Äänipainike (Block/SoundToggle) ohjaa mykistystä
 * videoRef-propin kautta.
 *
 * "Vähennä liikettä" (prefers-reduced-motion: reduce): ei automaattista
 * toistoa, poster jää näkyviin ja napautus käynnistää/pysäyttää videon
 * (WCAG 2.2.2). Äänipainikkeellisissa videoissa napautus hoituu
 * SoundTogglen kautta (tapToPlay={false}): ääni päälle käynnistää toiston.
 *
 * Props:
 *  - src: string                 // /videos/<nimi>.mp4
 *  - poster?: string             // oletuksena päätelty posterFor(src)
 *  - className?: string
 *  - videoRef?: React.RefObject  // jos vanhempi tarvitsee videoelementin
 *  - tapToPlay?: boolean         // oletus true: napautus käynnistää, kun
 *                                // automaattitoisto on pois (vähennä liikettä)
 */

// Osuus videosta, jonka pitää näkyä ennen toistoa (ja jonka alle se pysähtyy).
const VISIBLE_THRESHOLD = 0.25;

export const posterFor = (src) =>
  src.replace(/^\/videos\/(.+)\.mp4$/, "/images/posters/$1.jpg");

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const ViewportVideo = ({ src, poster, className, videoRef, tapToPlay = true }) => {
  const ownRef = useRef(null);
  const ref = videoRef || ownRef;
  const reducedMotion = prefersReducedMotion();

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    // Vähennä liikettä: ei automaattista toistoa. Pysäytys näkymästä
    // poistuessa pysyy, jotta käyttäjän käynnistämä video ei jää pyörimään.
    const autoplay = !prefersReducedMotion();
    // play() hylätään esim. iOS:n virransäästötilassa tai kun pause() ehtii
    // ensin — silloin poster jää näkyviin, ei virhettä konsoliin.
    const play = () => video.play().catch(() => {});

    if (!("IntersectionObserver" in window)) {
      if (autoplay) play();
      return undefined;
    }

    // Käynnistys kun neljännes näkyy, pysäytys vasta kun video on kokonaan
    // poistunut näkymästä, jolloin reunalla vieritys ei nyi toistoa päälle/pois.
    // Nopeassa vierityksessä samaan kutsuun voi kertyä useampi entry; vain
    // viimeinen kertoo nykyisen tilan.
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry.intersectionRatio >= VISIBLE_THRESHOLD) {
          if (autoplay) play();
        } else if (!entry.isIntersecting) {
          video.pause();
        }
      },
      { threshold: [0, VISIBLE_THRESHOLD] }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [ref, src]);

  // Napautus käynnistää tai pysäyttää, kun automaattitoisto on pois.
  const handleTap =
    reducedMotion && tapToPlay
      ? () => {
          const video = ref.current;
          if (!video) return;
          if (video.paused) video.play().catch(() => {});
          else video.pause();
        }
      : undefined;

  return (
    <video
      ref={ref}
      className={className}
      onClick={handleTap}
      poster={poster || posterFor(src)}
      loop
      muted
      playsInline
      preload="none"
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

export default ViewportVideo;
